import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import ProtectedRoute from "@/components/shared/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import FAQs from "@/pages/FAQs";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import VendorRegister from "@/pages/VendorRegister";
import LegalPage from "@/pages/LegalPage";

// Customer
import CustomerLayout from "@/pages/customer/CustomerLayout";
import CustomerHome from "@/pages/customer/CustomerHome";
import CustomerNewOrder from "@/pages/customer/CustomerNewOrder";
import CustomerOrders from "@/pages/customer/CustomerOrders";
import CustomerQuotes from "@/pages/customer/CustomerQuotes";
import CustomerTrack from "@/pages/customer/CustomerTrack";
import CustomerWallet from "@/pages/customer/CustomerWallet";
import CustomerDesigns from "@/pages/customer/CustomerDesigns";
import CustomerReviews from "@/pages/customer/CustomerReviews";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import CustomerSupport from "@/pages/customer/CustomerSupport";

// Vendor
import VendorLayout from "@/pages/vendor/VendorLayout";
import VendorHome from "@/pages/vendor/VendorHome";
import VendorBidding from "@/pages/vendor/VendorBidding";
import VendorOrders from "@/pages/vendor/VendorOrders";
import VendorEarnings from "@/pages/vendor/VendorEarnings";
import VendorProfile from "@/pages/vendor/VendorProfile";
import VendorSupport from "@/pages/vendor/VendorSupport";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: If OAuth returned with #session_id, route to AuthCallback synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Marketing shell */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/faqs" element={<FAQs />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/vendor/register" element={<VendorRegister />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/refund" element={<LegalPage kind="refund" />} />
      </Route>

      {/* Auth (bare) */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/role-select" element={<RoleSelect />} />

      {/* Customer dashboard */}
      <Route path="/customer" element={<ProtectedRoute role="customer"><CustomerLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/customer/dashboard" replace />} />
        <Route path="dashboard" element={<CustomerHome />} />
        <Route path="new-order" element={<CustomerNewOrder />} />
        <Route path="orders" element={<CustomerOrders />} />
        <Route path="quotes" element={<CustomerQuotes />} />
        <Route path="track" element={<CustomerTrack />} />
        <Route path="wallet" element={<CustomerWallet />} />
        <Route path="designs" element={<CustomerDesigns />} />
        <Route path="reviews" element={<CustomerReviews />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="support" element={<CustomerSupport />} />
      </Route>

      {/* Vendor dashboard */}
      <Route path="/vendor" element={<ProtectedRoute role="vendor"><VendorLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/vendor/dashboard" replace />} />
        <Route path="dashboard" element={<VendorHome />} />
        <Route path="bidding" element={<VendorBidding />} />
        <Route path="orders" element={<VendorOrders />} />
        <Route path="earnings" element={<VendorEarnings />} />
        <Route path="profile" element={<VendorProfile />} />
        <Route path="support" element={<VendorSupport />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
